/**
 * MetadataRecordDrawer Component
 *
 * Unified drawer for viewing, creating, and editing records.
 * 100% metadata-driven with lifecycle-aware editability.
 *
 * Features:
 * - View mode: All fields disabled (grayed out)
 * - Edit mode: Fields editable based on lifecycle state
 * - Create mode: Empty form for new records
 * - Collapsible sections grouped by form_section
 * - All action buttons in sticky footer (Save, Cancel, Lifecycle, Compare)
 * - "Create New Version" (duplicate) flow with cancel=delete
 *
 * This component is INTERNAL to MetadataTable.
 */

import { useState, useMemo, useCallback, useEffect } from 'react'
import type { Dayjs } from 'dayjs'
import {
  Drawer,
  Form,
  Button,
  Tag,
  Alert,
  Collapse,
  Divider,
  Grid,
  App,
} from 'antd'
import {
  CloseOutlined,
  DiffOutlined,
  CaretRightOutlined,
} from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import type {
  FieldMetadata,
  FormSection,
  DisplayMetadata,
  LifecycleMetadata,
  ApiConfig,
  ComparisonConfig,
} from '@/services/metadataService'
import { apiService } from '@/services/apiService'
import { MetadataFormField } from '@/components/MetadataForm/MetadataFormField'
import { LifecycleActions } from './LifecycleActions'
import { CreateLifecycleActions } from './CreateLifecycleActions'
import type { FormMode } from '@/components/MetadataForm/types'
import '../MetadataTable.css'

const { useBreakpoint } = Grid

// Date types that require conversion
const DATE_PY_TYPES = ['date', 'datetime', 'timestamp', 'timestamptz']

// ============================================================================
// Visual Design Constants
// ============================================================================

/** Enhanced state colors with backgrounds for visual prominence */
const STATE_STYLES: Record<
  string,
  { color: string; background: string; border: string }
> = {
  draft: {
    color: 'var(--rp-temporal-blue)',
    background: 'var(--rp-temporal-blue-soft)',
    border: 'var(--rp-temporal-blue-border)',
  },
  pending_approval: {
    color: 'var(--rp-temporal-orange)',
    background: 'var(--rp-temporal-orange-soft)',
    border: 'var(--rp-temporal-orange-border)',
  },
  active: {
    color: 'var(--rp-success)',
    background: 'var(--rp-success-soft)',
    border: 'var(--rp-success-border)',
  },
  expired: {
    color: 'var(--rp-gray-700)',
    background: 'var(--rp-surface-tertiary)',
    border: 'var(--rp-border-standard)',
  },
  superseded: {
    color: 'var(--rp-gray-500)',
    background: 'var(--rp-surface-muted)',
    border: 'var(--rp-border-subtle)',
  },
  pending: {
    color: 'var(--rp-temporal-orange)',
    background: 'var(--rp-temporal-orange-soft)',
    border: 'var(--rp-temporal-orange-border)',
  },
  completed: {
    color: 'var(--rp-success)',
    background: 'var(--rp-success-soft)',
    border: 'var(--rp-success-border)',
  },
  failed: {
    color: 'var(--rp-temporal-red)',
    background: 'var(--rp-temporal-red-soft)',
    border: 'var(--rp-temporal-red-border)',
  },
  cancelled: {
    color: 'var(--rp-gray-500)',
    background: 'var(--rp-surface-muted)',
    border: 'var(--rp-border-subtle)',
  },
}

/** Get state style with fallback */
function getStateStyle(state: string) {
  return (
    STATE_STYLES[state.toLowerCase()] || {
      color: 'var(--rp-gray-500)',
      background: 'var(--rp-surface-muted)',
      border: 'var(--rp-border-subtle)',
    }
  )
}

/**
 * Internal drawer state tracking
 */
interface DrawerInternalState {
  /** Whether user has made edits to the form */
  isDirty: boolean
  /** Original record that was duplicated from (for display purposes) */
  duplicatedFromRecord?: Record<string, unknown>
  /** Pre-filled values for create mode (used by duplicate action) */
  initialValues?: Record<string, unknown>
}

/**
 * Props for MetadataRecordDrawer
 */
export interface MetadataRecordDrawerProps {
  /** Whether drawer is open */
  open: boolean
  /** Close drawer callback */
  onClose: () => void

  /** API endpoint (e.g., '/offerings') */
  endpoint: string

  /** Mode: 'create' for new, 'edit' for modifying, 'view' for read-only */
  mode: FormMode

  /** Record data (required for edit/view modes) */
  record?: Record<string, unknown>

  /** Field metadata */
  fields: Record<string, FieldMetadata>

  /** Form sections for grouping */
  formSections?: FormSection[]

  /** Display metadata (label, icon, etc.) */
  display?: DisplayMetadata

  /** Lifecycle metadata */
  lifecycle?: LifecycleMetadata

  /** API configuration (for endpoint availability) */
  api?: ApiConfig

  /** Comparison configuration (for record versioning) */
  comparison?: ComparisonConfig

  /** Callback when Compare button is clicked */
  onCompare?: (record: Record<string, unknown>) => void

  /** Callback after successful save */
  onSuccess?: (savedRecord: Record<string, unknown>) => void

  /** Callback for actions that open an editor (e.g., duplicate) */
  onOpenEditor?: (record: Record<string, unknown>, actionKey: string) => void

  /** React Query key for cache invalidation */
  queryKey?: string[]

  /** Key field name (default: 'uid') */
  keyField?: string

  /** Internal state for duplicate flow - set by parent when handling duplicate action */
  internalState?: DrawerInternalState
}

/**
 * Default editable states for lifecycle tables
 */
const DEFAULT_EDITABLE_STATES = ['draft']

/**
 * Group fields by form_section for collapsible display
 */
function groupFieldsBySection(
  fields: Record<string, FieldMetadata>,
  formSections: FormSection[],
  mode: FormMode
): Array<{
  section: FormSection | null
  fields: Array<[string, FieldMetadata]>
}> {
  const groups: Map<string | null, Array<[string, FieldMetadata]>> = new Map()

  // Initialize groups for each section
  formSections.forEach((section) => {
    groups.set(section.name, [])
  })
  groups.set(null, []) // Orphan fields

  // Assign fields to groups
  // Note: system flag is for write protection only, not visibility
  Object.entries(fields).forEach(([key, metadata]) => {
    if (metadata.is_visible === false) return // Skip hidden fields

    // Check mode-based visibility (null/undefined = show in all modes)
    if (metadata.visible_in_modes && !metadata.visible_in_modes.includes(mode)) {
      return // Skip field not visible in current mode
    }

    const sectionName = metadata.form_section ?? null
    const group = groups.get(sectionName) ?? groups.get(null)!
    group.push([key, metadata])
  })

  // Sort fields within each group by form_order
  groups.forEach((fieldList) => {
    fieldList.sort((a, b) => (a[1].form_order ?? 99) - (b[1].form_order ?? 99))
  })

  // Build result in section order
  const result: Array<{
    section: FormSection | null
    fields: Array<[string, FieldMetadata]>
  }> = []

  // Add sections in order
  const sortedSections = [...formSections].sort((a, b) => a.order - b.order)
  sortedSections.forEach((section) => {
    const fieldList = groups.get(section.name)
    if (fieldList && fieldList.length > 0) {
      result.push({ section, fields: fieldList })
    }
  })

  // Add orphan fields at end
  const orphanFields = groups.get(null)
  if (orphanFields && orphanFields.length > 0) {
    result.push({ section: null, fields: orphanFields })
  }

  return result
}

/**
 * Determine the effective mode based on lifecycle and api config
 */
function determineEffectiveMode(
  requestedMode: FormMode,
  record: Record<string, unknown> | undefined,
  lifecycle: LifecycleMetadata | undefined,
  api: ApiConfig | undefined
): FormMode {
  // Create mode stays as create
  if (requestedMode === 'create' || !record) return 'create'

  // Check if update is disabled at API level
  if (api?.endpoints?.update === false) return 'view'

  // No lifecycle = allow requested mode
  if (!lifecycle?.enabled) return requestedMode

  // Check editable states
  const editableStates = DEFAULT_EDITABLE_STATES
  const currentState = record[lifecycle.stateField] as string

  if (currentState && editableStates.includes(currentState)) {
    return requestedMode // Allow edit if requested
  }

  return 'view' // Force view for non-editable states
}

/**
 * MetadataRecordDrawer - Unified record drawer for create/edit/view
 */
export function MetadataRecordDrawer({
  open,
  onClose,
  endpoint,
  mode: requestedMode,
  record,
  fields,
  formSections = [],
  display,
  lifecycle,
  api,
  comparison,
  onCompare,
  onSuccess,
  onOpenEditor,
  queryKey,
  keyField = 'uid',
  internalState,
}: MetadataRecordDrawerProps) {
  const { modal, message } = App.useApp()
  const [form] = Form.useForm()
  const screens = useBreakpoint()
  const queryClient = useQueryClient()

  // Submission state
  const [submitting, setSubmitting] = useState(false)

  // Track if user has modified the form
  const [isDirty, setIsDirty] = useState(false)

  // Track effective date values for cross-field validation
  // These are used to constrain effectiveTo >= effectiveFrom
  const [effectiveFromValue, setEffectiveFromValue] = useState<Dayjs | null>(null)
  const [effectiveToValue, setEffectiveToValue] = useState<Dayjs | null>(null)

  // Determine effective mode based on lifecycle state
  const effectiveMode = useMemo(
    () => determineEffectiveMode(requestedMode, record, lifecycle, api),
    [requestedMode, record, lifecycle, api]
  )

  const isViewMode = effectiveMode === 'view'
  const isCreateMode = effectiveMode === 'create'
  const isDuplicateCreate = isCreateMode && !!internalState?.duplicatedFromRecord

  // Responsive drawer width
  const drawerWidth = useMemo(() => {
    if (!screens.md) return '100%' // Mobile: full screen
    if (!screens.lg) return '90vw' // Tablet: 90%
    return 700 // Desktop: fixed width
  }, [screens])

  // Group fields by section (filtered by current mode)
  const groupedFields = useMemo(
    () => groupFieldsBySection(fields, formSections, effectiveMode),
    [fields, formSections, effectiveMode]
  )

  // Default open panels (all sections)
  const defaultActiveKeys = useMemo(
    () => groupedFields.map((g, i) => g.section?.name ?? `other-${i}`),
    [groupedFields]
  )

  // Get display title
  const drawerTitle = useMemo(() => {
    const entityName = display?.label_singular ?? 'Record'
    if (isCreateMode) return `Create ${entityName}`
    if (isViewMode) return `${entityName} Details`
    return `Edit ${entityName}`
  }, [display, isCreateMode, isViewMode])

  // Get current lifecycle state
  const currentState = useMemo(() => {
    if (!lifecycle?.enabled || !record) return null
    return record[lifecycle.stateField] as string | undefined
  }, [lifecycle, record])

  // Set initial form values when record changes
  useEffect(() => {
    if (!open) return

    if (record && !isCreateMode) {
      // Filter and transform values for form
      const validFields = Object.keys(record).filter(
        (key) => fields[key] && fields[key].is_visible !== false
      )
      const formValues = Object.fromEntries(
        validFields.map((key) => {
          const value = record[key]
          const fieldMeta = fields[key]

          // Convert date strings to dayjs objects
          const isDateField =
            fieldMeta &&
            (DATE_PY_TYPES.includes(fieldMeta.py_type) ||
              fieldMeta.component_type === 'datepicker')
          if (value && isDateField) {
            return [key, dayjs(value as string | number | Date)]
          }

          return [key, value]
        })
      )
      form.setFieldsValue(formValues)

      // Initialize effective date state from record values
      if (formValues.effectiveFrom !== undefined) {
        setEffectiveFromValue(formValues.effectiveFrom as Dayjs | null)
      }
      if (formValues.effectiveTo !== undefined) {
        setEffectiveToValue(formValues.effectiveTo as Dayjs | null)
      }
    } else if (isCreateMode) {
      // Start with defaults from schema
      const defaults = Object.entries(fields)
        .filter(([, field]) => field.default !== undefined)
        .reduce(
          (acc, [key, field]) => {
            acc[key] = field.default
            return acc
          },
          {} as Record<string, unknown>
        )

      // If we have initialValues (from duplicate), merge them on top of defaults
      // and convert date fields to dayjs
      if (internalState?.initialValues) {
        const initialValues = { ...internalState.initialValues }
        Object.keys(initialValues).forEach((key) => {
          const value = initialValues[key]
          const fieldMeta = fields[key]
          if (!fieldMeta) return

          // Convert date strings to dayjs objects
          const isDateField =
            DATE_PY_TYPES.includes(fieldMeta.py_type) ||
            fieldMeta.component_type === 'datepicker'
          if (value && isDateField) {
            initialValues[key] = dayjs(value as string | number | Date)
          }
        })
        Object.assign(defaults, initialValues)
      }

      form.setFieldsValue(defaults)

      // Initialize effective date state from defaults/initialValues
      if (defaults.effectiveFrom !== undefined) {
        setEffectiveFromValue(defaults.effectiveFrom as Dayjs | null)
      } else {
        setEffectiveFromValue(null)
      }
      if (defaults.effectiveTo !== undefined) {
        setEffectiveToValue(defaults.effectiveTo as Dayjs | null)
      } else {
        setEffectiveToValue(null)
      }
    }

    // Reset dirty state when opening
    setIsDirty(false)
  }, [open, record, fields, form, isCreateMode, internalState?.initialValues])

  // Handle form value changes (track dirty state and effective dates)
  const handleValuesChange = useCallback(
    (changedValues: Record<string, unknown>) => {
      setIsDirty(true)

      // Track effective date changes for cross-field validation
      if ('effectiveFrom' in changedValues) {
        setEffectiveFromValue(changedValues.effectiveFrom as Dayjs | null)
      }
      if ('effectiveTo' in changedValues) {
        setEffectiveToValue(changedValues.effectiveTo as Dayjs | null)
      }
    },
    []
  )

  // Handle form submission
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()

      // Cross-field validation: effectiveFrom <= effectiveTo
      const effectiveFrom = values.effectiveFrom as Dayjs | null | undefined
      const effectiveTo = values.effectiveTo as Dayjs | null | undefined

      if (effectiveFrom && effectiveTo && effectiveFrom.isAfter(effectiveTo, 'day')) {
        message.error('Effective From date must be on or before Effective To date')
        return
      }

      setSubmitting(true)

      // Transform values for API
      // Note: system flag controls write protection at backend level (field_rules.py)
      // Frontend skips readonly fields; backend enforces system field protection
      const payload = Object.fromEntries(
        Object.entries(values)
          .filter(([key, value]) => {
            // Skip undefined values
            if (value === undefined) return false
            // Skip fields not in metadata (hidden system fields like keyComboHash)
            if (!fields[key]) return false
            // Skip readonly fields (like lifecycleState) - they're managed by lifecycle actions
            if (fields[key].readonly) return false
            // Skip non-writable fields based on category (lifecycle, computed, audit, etc.)
            // This uses the category-derived effective_writable flag from backend
            if (fields[key].effective_writable === false) return false
            return true
          })
          .map(([key, value]) => {
            // Convert dayjs to ISO string
            if (value && dayjs.isDayjs(value)) {
              return [key, value.toISOString()]
            }
            return [key, value]
          })
      )

      // Add version for optimistic locking in edit mode
      if (!isCreateMode && record?.version !== undefined) {
        payload.version = record.version
      }

      // Make API call
      const url = isCreateMode
        ? endpoint
        : `${endpoint}/${record?.[keyField]}`

      const response = isCreateMode
        ? await apiService.post<Record<string, unknown>>(url, payload)
        : await apiService.put<Record<string, unknown>>(url, payload)

      if (response.success && response.data) {
        message.success(
          isCreateMode
            ? `${display?.label_singular ?? 'Record'} created successfully`
            : `${display?.label_singular ?? 'Record'} updated successfully`
        )

        // Invalidate queries
        const key = queryKey || ['tableData', endpoint]
        queryClient.invalidateQueries({ queryKey: key })
        queryClient.invalidateQueries({
          queryKey: ['record', endpoint],
          exact: false,
        })

        // Reset form and close
        form.resetFields()
        setIsDirty(false)
        onSuccess?.(response.data)
        onClose()
      }
    } catch (error) {
      // Form validation failed or API error
      console.error('Form submission failed:', error)
    } finally {
      setSubmitting(false)
    }
  }

  // Handle close with dirty check
  const handleClose = useCallback(() => {
    // If dirty (has unsaved changes), confirm discard
    if (isDirty && !isViewMode) {
      modal.confirm({
        title: 'Discard Changes?',
        content: 'You have unsaved changes. Are you sure you want to close?',
        okText: 'Discard',
        okType: 'danger',
        cancelText: 'Keep Editing',
        onOk: () => {
          form.resetFields()
          setIsDirty(false)
          onClose()
        },
      })
      return
    }

    // Clean close
    form.resetFields()
    setIsDirty(false)
    onClose()
  }, [isDirty, isViewMode, form, onClose])

  // Handle lifecycle action that opens editor (e.g., duplicate)
  // This delegates to the parent (MetadataTable) which manages drawer state
  const handleOpenEditor = useCallback(
    (editorRecord: Record<string, unknown>, actionKey: string) => {
      // Delegate to parent handler - parent will reconfigure drawer state
      // for duplicate actions (open in create mode with pre-filled values)
      if (onOpenEditor) {
        onOpenEditor(editorRecord, actionKey)
      }
    },
    [onOpenEditor]
  )

  // Get validated form values for create lifecycle actions (Create & Activate)
  const getFormValues = useCallback(async (): Promise<Record<string, unknown> | null> => {
    try {
      const values = await form.validateFields()

      // Transform values for API (same logic as handleSubmit)
      // Note: system flag controls write protection at backend level (field_rules.py)
      const payload = Object.fromEntries(
        Object.entries(values)
          .filter(([key, value]) => {
            if (value === undefined) return false
            // Skip fields not in metadata (hidden system fields)
            if (!fields[key]) return false
            if (fields[key].readonly) return false
            // Skip non-writable fields based on category (lifecycle, computed, audit, etc.)
            if (fields[key].effective_writable === false) return false
            return true
          })
          .map(([key, value]) => {
            if (value && dayjs.isDayjs(value)) {
              return [key, value.toISOString()]
            }
            return [key, value]
          })
      )

      return payload
    } catch {
      // Form validation failed
      return null
    }
  }, [form, fields])

  // Handle success from CreateLifecycleActions (Create & Activate flow)
  const handleCreateLifecycleSuccess = useCallback(
    (savedRecord: Record<string, unknown>) => {
      // Invalidate queries
      const key = queryKey || ['tableData', endpoint]
      queryClient.invalidateQueries({ queryKey: key })
      queryClient.invalidateQueries({
        queryKey: ['record', endpoint],
        exact: false,
      })

      // Reset form and close
      form.resetFields()
      setIsDirty(false)
      onSuccess?.(savedRecord)
      onClose()
    },
    [queryKey, endpoint, queryClient, form, onSuccess, onClose]
  )

  // Check if there are lifecycle actions available for current state
  const hasLifecycleActions = useMemo(() => {
    if (!lifecycle?.enabled || !currentState || !lifecycle.actions) return false
    return Object.values(lifecycle.actions).some((action) =>
      action.fromStates.includes(currentState)
    )
  }, [lifecycle, currentState])

  // Check if there are lifecycle actions available for create mode (from 'draft' state)
  // Excludes 'duplicate' since that doesn't make sense for create
  const hasCreateLifecycleActions = useMemo(() => {
    if (!lifecycle?.enabled || !lifecycle.actions) return false
    return Object.entries(lifecycle.actions).some(
      ([key, action]) => key !== 'duplicate' && action.fromStates.includes('draft')
    )
  }, [lifecycle])

  // Check if compare is available
  const hasCompare = comparison?.enabled && onCompare && !isCreateMode && record

  // Determine if we need a divider in footer (between form actions and other actions)
  const showFooterDivider =
    !isViewMode && (hasLifecycleActions || hasCompare || (isCreateMode && hasCreateLifecycleActions))

  return (
    <Drawer
      title={drawerTitle}
      placement="right"
      width={drawerWidth}
      open={open}
      onClose={handleClose}
      closable={true}
      closeIcon={<CloseOutlined />}
      destroyOnHidden
      className="metadata-record-drawer"
      styles={{
        header: {
          borderBottom: '1px solid var(--rp-border-subtle)',
          background: 'var(--rp-surface-tertiary)',
        },
        body: {
          background: 'var(--rp-surface-primary)',
          padding: screens.md ? 24 : 16,
        },
        footer: {
          borderTop: '1px solid var(--rp-border-subtle)',
          background: 'linear-gradient(180deg, var(--rp-surface-primary) 0%, var(--rp-surface-tertiary) 100%)',
          padding: screens.md ? '16px 24px' : '12px 16px',
        },
      }}
      extra={
        // Header only shows state badge (info only, no actions)
        !isCreateMode && record && lifecycle?.enabled && currentState ? (
          <Tag
            style={{
              padding: '4px 12px',
              fontSize: 13,
              fontWeight: 500,
              border: `1px solid ${getStateStyle(currentState).border}`,
              background: getStateStyle(currentState).background,
              color: getStateStyle(currentState).color,
              borderRadius: 4,
            }}
          >
            {currentState.charAt(0).toUpperCase() + currentState.slice(1)}
          </Tag>
        ) : null
      }
      footer={
        // All actions in sticky footer
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          {/* Left side: Lifecycle actions + Compare */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* Lifecycle action buttons - for edit/view mode */}
            {!isCreateMode && record && lifecycle?.enabled && currentState && (
              <LifecycleActions
                lifecycle={lifecycle}
                record={record}
                endpoint={endpoint}
                entityLabel={display?.label_singular ?? 'Record'}
                fieldMetadata={fields}
                comparisonKeyFields={comparison?.keyFields}
                timelineLabelField={comparison?.timelineLabelField}
                onSuccess={() => {
                  const key = queryKey || ['tableData', endpoint]
                  queryClient.invalidateQueries({ queryKey: key })
                }}
                onOpenEditor={handleOpenEditor}
              />
            )}

            {/* Lifecycle action buttons - for create mode (Create & Activate, etc.) */}
            {isCreateMode && lifecycle?.enabled && hasCreateLifecycleActions && (
              <CreateLifecycleActions
                lifecycle={lifecycle}
                endpoint={endpoint}
                entityLabel={display?.label_singular ?? 'Record'}
                keyField={keyField}
                getFormValues={getFormValues}
                onSuccess={handleCreateLifecycleSuccess}
                disabled={submitting}
              />
            )}

            {/* Compare button */}
            {hasCompare && (
              <Button
                icon={<DiffOutlined />}
                onClick={() => onCompare!(record!)}
                title="Compare with other versions"
              >
                Compare
              </Button>
            )}
          </div>

          {/* Right side: Cancel/Close + Save/Create */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {showFooterDivider && (
              <Divider type="vertical" style={{ height: 24, margin: '0 4px' }} />
            )}
            <Button
              onClick={handleClose}
              style={{
                minWidth: 80,
                height: 36,
              }}
            >
              {isViewMode ? 'Close' : 'Cancel'}
            </Button>
            {!isViewMode && (
              <Button
                type="primary"
                onClick={handleSubmit}
                loading={submitting}
                style={{
                  minWidth: 120,
                  height: 36,
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(24, 144, 255, 0.2)',
                }}
              >
                {isCreateMode ? 'Create' : 'Save Changes'}
              </Button>
            )}
          </div>
        </div>
      }
    >
      {/* Info banner for duplicate-based create */}
      {isDuplicateCreate && !!internalState?.duplicatedFromRecord && (
        <Alert
          type="info"
          showIcon
          style={{
            marginBottom: 20,
            borderRadius: 8,
          }}
          message={
            <span style={{ fontWeight: 600 }}>
              Creating New Version
            </span>
          }
          description={
            <span style={{ color: '#5e7492' }}>
              Pre-filled from "
              {(internalState.duplicatedFromRecord.name as string) ||
                (internalState.duplicatedFromRecord[keyField] as string)}
              ". Make your changes and click Create to save.
            </span>
          }
        />
      )}

      {/* Supersession banner for superseded records */}
      {!isCreateMode && record?.lifecycleState === 'superseded' && !!record?.supersededByUid && (
        <Alert
          type="info"
          showIcon
          style={{
            marginBottom: 20,
            borderRadius: 8,
          }}
          message={
            <span style={{ fontWeight: 600 }}>
              This record has been superseded
            </span>
          }
          description={
            <span style={{ color: '#5e7492' }}>
              A newer version is now active. This record is kept for historical reference.
            </span>
          }
        />
      )}

      {/* Form */}
      <Form form={form} layout="vertical" onValuesChange={handleValuesChange}>
        {/* Collapsible sections */}
        {groupedFields.length > 0 && (
          <Collapse
            defaultActiveKey={defaultActiveKeys}
            bordered={false}
            style={{
              background: 'transparent',
            }}
            expandIcon={({ isActive }) => (
              <CaretRightOutlined
                rotate={isActive ? 90 : 0}
                style={{ color: '#1890ff', fontSize: 12 }}
              />
            )}
            items={groupedFields.map((group, index) => {
              const panelKey = group.section?.name ?? `other-${index}`
              const panelHeader = group.section?.name ?? 'Other Fields'

              return {
                key: panelKey,
                label: (
                  <span
                    style={{
                      fontWeight: 600,
                      fontSize: 14,
                      color: '#1e3a5f',
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {panelHeader}
                  </span>
                ),
                extra: group.section?.description ? (
                  <span
                    style={{
                      color: '#5e7492',
                      fontSize: 12,
                      fontWeight: 400,
                    }}
                  >
                    {group.section.description}
                  </span>
                ) : undefined,
                style: {
                  marginBottom: 12,
                  background: 'var(--rp-surface-tertiary-gradient)',
                  borderRadius: 8,
                  border: '1px solid var(--rp-border-standard)',
                  overflow: 'hidden',
                },
                children: (
                  <div style={{ padding: '4px 0' }}>
                    {group.fields.map(([fieldName, metadata]) => {
                      // Compute date constraints for effectiveFrom/effectiveTo fields
                      let minDate: Dayjs | null | undefined
                      let maxDate: Dayjs | null | undefined
                      let disabledDateMessage: string | undefined

                      if (fieldName === 'effectiveFrom' && effectiveToValue) {
                        // effectiveFrom must be <= effectiveTo
                        maxDate = effectiveToValue
                        disabledDateMessage = 'Must be on or before Effective To date'
                      } else if (fieldName === 'effectiveTo' && effectiveFromValue) {
                        // effectiveTo must be >= effectiveFrom
                        minDate = effectiveFromValue
                        disabledDateMessage = 'Must be on or after Effective From date'
                      }

                      return (
                        <MetadataFormField
                          key={fieldName}
                          fieldName={fieldName}
                          metadata={metadata}
                          disabled={isViewMode || metadata.readonly === true}
                          minDate={minDate}
                          maxDate={maxDate}
                          disabledDateMessage={disabledDateMessage}
                        />
                      )
                    })}
                  </div>
                ),
              }
            })}
          />
        )}

        {/* No fields message */}
        {groupedFields.length === 0 && (
          <Alert
            type="info"
            message="No fields available"
            description="This record type has no editable fields defined."
            style={{ borderRadius: 8 }}
          />
        )}
      </Form>
    </Drawer>
  )
}

export default MetadataRecordDrawer
